import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './SpaceMission.css';

// Contract ABIs
const SPACE_MISSION_ABI = [
  "function launchMission(uint8 destination, uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 destination, uint8 result, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256[5] missionSuccessRates, uint256[5] missionPayouts))",
  "function getMissionInfo(uint8 destination) external view returns (uint256 successRate, uint256 payout, string name, string description)",
  "function calculateHouseEdge() external view returns (uint256)",
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)", 
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function updateMissionSuccessRates(uint256[5] newRates) external",
  "function updateMissionPayouts(uint256[5] newPayouts) external",
  "function updateNoWinChance(uint256 newNoWinChance) external",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 destination, uint8 result, uint256 grossPayout, uint256 netPayout, bool won)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  SPACE_MISSION: "0xd2C8626595C7DF94E6f7bcb19b47739544A1efeb", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// Mission destinations configuration
const SPACE_DESTINATIONS = [
  { 
    id: 0, 
    name: 'MOON', 
    icon: '🌙', 
    color: '#C0C0C0', 
    description: 'Safe lunar landing - Low risk, steady reward', 
    distance: '384,400 km',
    riskLevel: 'LOW',
    baseMultiplier: '2x',
    atmosphere: 'None'
  },
  { 
    id: 1, 
    name: 'MARS', 
    icon: '🔴', 
    color: '#CD5C5C', 
    description: 'Mars colonization - Medium risk, good reward', 
    distance: '225M km',
    riskLevel: 'MEDIUM',
    baseMultiplier: '5x',
    atmosphere: 'Thin CO₂'
  },
  { 
    id: 2, 
    name: 'JUPITER', 
    icon: '🟠', 
    color: '#FFA500', 
    description: 'Jupiter exploration - High risk, great reward', 
    distance: '778M km',
    riskLevel: 'HIGH',
    baseMultiplier: '12x',
    atmosphere: 'Dense Gas'
  },
  { 
    id: 3, 
    name: 'ALPHA CENTAURI', 
    icon: '⭐', 
    color: '#FFD700', 
    description: 'Interstellar voyage - Extreme risk, massive reward', 
    distance: '4.37 LY',
    riskLevel: 'EXTREME',
    baseMultiplier: '50x',
    atmosphere: 'Stellar'
  },
  { 
    id: 4, 
    name: 'BLACK HOLE', 
    icon: '⚫', 
    color: '#4B0082', 
    description: 'Black hole study - Ultimate risk, legendary reward', 
    distance: '26K LY',
    riskLevel: 'ULTIMATE',
    baseMultiplier: '100x',
    atmosphere: 'Singularity'
  }
];

// 🚀 SPACE MISSION SOUND SYSTEM 🚀
const playSpaceSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'rocketIgnition':
        createComplexSound([
          { freq: 80, duration: 1.2, wave: 'sawtooth', delay: 0 },
          { freq: 120, duration: 1.0, wave: 'triangle', delay: 200 },
          { freq: 200, duration: 0.8, wave: 'sine', delay: 400 },
          { freq: 300, duration: 0.6, wave: 'triangle', delay: 600 }
        ], 0.8);
        break;
        
      case 'rocketFlight':
        createComplexSound([
          { freq: 400, duration: 2.0, wave: 'triangle', delay: 0 },
          { freq: 300, duration: 2.2, wave: 'sine', delay: 100 },
          { freq: 500, duration: 1.8, wave: 'triangle', delay: 200 }
        ], 1.5);
        break;
        
      case 'planetLanding':
        createComplexSound([
          { freq: 200, duration: 0.8, wave: 'sine', delay: 0 },
          { freq: 150, duration: 1.0, wave: 'triangle', delay: 200 },
          { freq: 100, duration: 1.2, wave: 'sine', delay: 400 }
        ], 0.6);
        break;
        
      case 'missionSuccess':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'triangle', delay: 200 },
          { freq: 784, duration: 0.5, wave: 'sine', delay: 400 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 600 },
          { freq: 1319, duration: 0.7, wave: 'sine', delay: 800 }
        ], 0.5);
        break;
        
      case 'missionFailure':
        createComplexSound([
          { freq: 220, duration: 0.8, wave: 'sawtooth', delay: 0 },
          { freq: 110, duration: 1.0, wave: 'triangle', delay: 300 },
          { freq: 55, duration: 1.2, wave: 'sine', delay: 600 }
        ], 0.6);
        break;
        
      case 'blackHole':
        createComplexSound([
          { freq: 40, duration: 2.0, wave: 'sawtooth', delay: 0 },
          { freq: 80, duration: 1.8, wave: 'triangle', delay: 100 },
          { freq: 160, duration: 1.5, wave: 'sine', delay: 200 },
          { freq: 320, duration: 1.2, wave: 'triangle', delay: 400 }
        ], 1.0);
        break;
        
      case 'starField':
        createTone(800, 0.1, 'sine');
        break;
        
      case 'controlPanel':
        createComplexSound([
          { freq: 800, duration: 0.1, wave: 'square', delay: 0 },
          { freq: 1000, duration: 0.08, wave: 'triangle', delay: 30 }
        ], 0.08);
        break;
        
      case 'spaceAmbient':
        createTone(60, 0.2, 'sine');
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const SpaceMission = ({ walletData, onBack }) => {
  const [missionState, setMissionState] = useState({
    fuelAmount: '10',
    selectedDestination: null,
    isLaunching: false,
    isApproving: false,
    lastMission: null,
    balance: '0',
    allowance: '0',
    commanderRank: 1,
    missionPoints: 0,
    totalMissions: 0,
    successfulMissions: 0,
    showLaunchModal: false,
    systemReadiness: 0
  });

  const [spaceStats, setSpaceStats] = useState({
    totalMissions: 0,
    totalFuel: '0',
    totalRewards: '0',
    houseEdge: '15',
    bestMultiplier: '2.00',
    activeExplorers: 0
  });

  const [missionHistory, setMissionHistory] = useState([]);
  const [launchPhase, setLaunchPhase] = useState('idle');
  const [missionOutcome, setMissionOutcome] = useState(null);
  const [cosmicParticles, setCosmicParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    spaceMission: null,
    blbToken: null
  });
  
  // Admin Panel State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminState, setAdminState] = useState({
    houseBalance: '0',
    isPaused: false,
    fundAmount: '',
    withdrawAmount: '',
    minBet: '1',
    maxBet: '1000000',
    newMissionRates: {
      moon: '80.0',
      mars: '50.0', 
      jupiter: '25.0',
      alphaCentauri: '8.0',
      blackHole: '3.0'
    },
    newMissionPayouts: {
      moon: '200',
      mars: '500',
      jupiter: '1200', 
      alphaCentauri: '5000',
      blackHole: '10000'
    }
  });

  const rocketRef = useRef(null);
  const spaceViewRef = useRef(null);

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playSpaceSound(type, volume);
    }
  };

  // Cosmic particle effects
  useEffect(() => {
    const generateCosmicParticles = () => {
      const particles = [];
      const particleCount = 40 + (missionState.commanderRank * 5);
      
      const particleTypes = ['✨', '⭐', '💫', '🌟', '🔸', '💎', '🌌'];
      
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          id: i,
          type: particleTypes[Math.floor(Math.random() * particleTypes.length)],
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 15 + 10,
          speed: Math.random() * 0.5 + 0.3,
          opacity: Math.random() * 0.6 + 0.2,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 1
        });
      }
      setCosmicParticles(particles);
    };

    generateCosmicParticles();
    
    const interval = setInterval(() => {
      setCosmicParticles(prev => prev.map(particle => ({
        ...particle,
        y: (particle.y + particle.speed * 0.05) % 105,
        x: particle.x + Math.sin(Date.now() * 0.001 + particle.id) * 0.1,
        rotation: (particle.rotation + particle.rotationSpeed) % 360,
        opacity: 0.2 + Math.sin(Date.now() * 0.003 + particle.id) * 0.4
      })));
    }, 100);

    return () => clearInterval(interval);
  }, [missionState.commanderRank]);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ spaceMission: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const spaceMissionContract = new ethers.Contract(
        CONTRACT_ADDRESSES.SPACE_MISSION,
        SPACE_MISSION_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        spaceMission: spaceMissionContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadSpaceData(spaceMissionContract);
        loadBalance(blbTokenContract, spaceMissionContract);
        checkAdminAccess(spaceMissionContract);
      }, 500);

    } catch (error) {
      console.log('Error initializing contracts:', error);
      setContracts({ spaceMission: null, blbToken: null });
    }
  };

  // Check admin access
  const checkAdminAccess = async (contract = contracts.spaceMission) => {
    if (!contract || !walletData?.account) return;

    try {
      const owner = await contract.owner();
      const userIsOwner = owner.toLowerCase() === walletData.account.toLowerCase();
      
      const admin = await contract.admin();
      const userIsAdmin = admin.toLowerCase() === walletData.account.toLowerCase();

      setIsOwner(userIsOwner);
      setIsAdmin(userIsAdmin);

      if (userIsOwner || userIsAdmin) {
        await loadAdminData(contract);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsOwner(false);
      setIsAdmin(false);
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.spaceMission) => {
    if (!contract) return;

    try {
      const [houseBalance, isPaused, betLimits] = await Promise.all([
        contract.houseBalance(),
        contract.paused(),
        Promise.all([contract.minBet(), contract.maxBet()])
      ]);

      setAdminState(prev => ({
        ...prev,
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(betLimits[0]),
        maxBet: ethers.utils.formatEther(betLimits[1])
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Load space data
  const loadSpaceData = async (contract = contracts.spaceMission) => {
    if (!contract) return;

    try {
      const [stats, recentMissions, config] = await Promise.all([
        contract.getGameStats(),
        contract.getRecentGames(5).catch(() => []),
        contract.getGameConfig()
      ]);

      setSpaceStats({
        totalMissions: parseInt(stats.totalGames.toString()),
        totalFuel: ethers.utils.formatEther(stats.totalBets),
        totalRewards: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: '15',
        bestMultiplier: '2.00',
        activeExplorers: Math.floor(Math.random() * 120) + 25
      });

      if (recentMissions && recentMissions.length > 0) {
        const formattedHistory = recentMissions.slice(0, 5).map(mission => ({
          destination: parseInt(mission.destination),
          result: parseInt(mission.result),
          won: mission.won,
          fuelAmount: ethers.utils.formatEther(mission.betAmount),
          rewards: ethers.utils.formatEther(mission.netPayout),
          timestamp: parseInt(mission.timestamp) * 1000
        }));
        setMissionHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading space data:', error);
    }
  };

  // Load balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.spaceMission) => {
    if (!tokenContract || !gameContract || !walletData?.account) return;

    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(walletData.account),
        tokenContract.allowance(walletData.account, gameContract.address)
      ]);

      setMissionState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  // Handle mission launch
  const handleLaunchMission = async () => {
    if (!contracts.spaceMission || !contracts.blbToken || missionState.selectedDestination === null) {
      setErrorMessage('Mission control unavailable. Please reconnect.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const fuelAmount = parseFloat(missionState.fuelAmount);
      const balance = parseFloat(missionState.balance);

      if (fuelAmount <= 0) {
        setErrorMessage('Fuel amount must be greater than 0');
        return;
      }

      if (fuelAmount > balance) {
        setErrorMessage('Insufficient fuel reserves');
        return;
      }

      // Start launch sequence
      playGameSound('rocketIgnition', 0.6);
      
      setMissionState(prev => ({ 
        ...prev, 
        isLaunching: true,
        showLaunchModal: true,
        systemReadiness: 0
      }));
      setLaunchPhase('preparation');

      // System readiness buildup
      const readinessInterval = setInterval(() => {
        setMissionState(prev => ({
          ...prev,
          systemReadiness: Math.min(prev.systemReadiness + 10, 100)
        }));
      }, 100);

      setTimeout(() => {
        clearInterval(readinessInterval);
        executeMission();
      }, 3000);

    } catch (error) {
      console.error('Mission launch error:', error);
      setLaunchPhase('idle');
      setMissionState(prev => ({ 
        ...prev, 
        isLaunching: false,
        showLaunchModal: false,
        systemReadiness: 0
      }));
    }
  };

  const executeMission = async () => {
    try {
      playGameSound('rocketFlight', 0.7);
      setLaunchPhase('launch');
      setMissionState(prev => ({ ...prev, systemReadiness: 0 }));

      const fuelAmountWei = ethers.utils.parseEther(missionState.fuelAmount);
      const tx = await contracts.spaceMission.launchMission(missionState.selectedDestination, fuelAmountWei);
      
      setSuccessMessage('🚀 Mission launched! Rocket en route to destination...');
      
      const receipt = await tx.wait();
      
      const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
      
      if (gamePlayedEvent) {
        const [player, betAmount, destination, result, grossPayout, netPayout, won] = gamePlayedEvent.args;
        
        const missionResult = {
          player,
          fuelAmount: ethers.utils.formatEther(betAmount),
          destination: parseInt(destination),
          result: parseInt(result),
          grossPayout: ethers.utils.formatEther(grossPayout),
          netPayout: ethers.utils.formatEther(netPayout),
          won,
          timestamp: Date.now()
        };

        setTimeout(() => {
          handleMissionResult(missionResult);
        }, 2500);
        
        await Promise.all([loadSpaceData(), loadBalance()]);
      } else {
        throw new Error('Mission telemetry not found');
      }

    } catch (error) {
      console.error('Mission execution error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Mission aborted by commander');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient fuel or gas');
      } else if (error.message.includes('allowance')) {
        setErrorMessage('Please approve fuel usage first');
      } else {
        setErrorMessage(`Mission failed: ${error.reason || error.message}`);
      }
      
      setLaunchPhase('idle');
      setMissionState(prev => ({ 
        ...prev, 
        isLaunching: false,
        showLaunchModal: false
      }));
      setSuccessMessage('');
    }
  };

  const handleMissionResult = (missionResult) => {
    setMissionOutcome(missionResult);
    setLaunchPhase('result');

    setTimeout(() => {
      if (missionResult.won) {
        if (missionResult.destination === 4) { // Black Hole
          playGameSound('blackHole', 0.9);
          setSuccessMessage(`🌌 BLACK HOLE MISSION SUCCESS! Legendary discovery! +${formatNumber(missionResult.netPayout)} BLB`);
          setMissionState(prev => ({
            ...prev,
            missionPoints: prev.missionPoints + 5000
          }));
        } else {
          playGameSound('missionSuccess', 0.8);
          const destName = SPACE_DESTINATIONS[missionResult.destination]?.name || 'DESTINATION';
          setSuccessMessage(`🚀 ${destName} MISSION SUCCESS! Commander! +${formatNumber(missionResult.netPayout)} BLB`);
          setMissionState(prev => ({
            ...prev,
            missionPoints: prev.missionPoints + (500 * (missionResult.destination + 1)),
            successfulMissions: prev.successfulMissions + 1
          }));
        }
      } else {
        playGameSound('missionFailure', 0.4);
        setErrorMessage(`💥 Mission failed! Rocket lost in space! -${formatNumber(missionResult.fuelAmount)} BLB`);
        setMissionState(prev => ({
          ...prev,
          missionPoints: prev.missionPoints + 50
        }));
      }
    }, 500);

    setMissionState(prev => ({
      ...prev,
      isLaunching: false,
      totalMissions: prev.totalMissions + 1,
      commanderRank: Math.floor((prev.missionPoints + (missionResult.won ? 500 : 50)) / 3000) + 1
    }));

    setTimeout(() => {
      setLaunchPhase('idle');
      setMissionOutcome(null);
      setMissionState(prev => ({ 
        ...prev, 
        isLaunching: false,
        showLaunchModal: false,
        selectedDestination: null
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 5000);
  };

  // Handle token approval
  const handleApprove = async () => {
    if (!contracts.blbToken || !contracts.spaceMission) {
      setErrorMessage('Mission control unavailable. Please reconnect.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('controlPanel', 0.3);
      setMissionState(prev => ({ ...prev, isApproving: true }));

      const maxAmount = ethers.constants.MaxUint256;
      const tx = await contracts.blbToken.approve(contracts.spaceMission.address, maxAmount);
      
      setSuccessMessage('Mission authorization processing...');
      await tx.wait();
      
      await loadBalance();
      setSuccessMessage('✅ Mission authorization granted! Ready for launch!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Mission authorization denied');
      } else {
        setErrorMessage(`Authorization failed: ${error.reason || error.message}`);
      }
    } finally {
      setMissionState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const needsApproval = () => {
    return parseFloat(missionState.allowance) < parseFloat(missionState.fuelAmount);
  };

  const canLaunchMission = () => {
    return connected && 
           contracts.spaceMission &&
           contracts.blbToken &&
           missionState.selectedDestination !== null &&
           parseFloat(missionState.balance) >= parseFloat(missionState.fuelAmount) &&
           parseFloat(missionState.fuelAmount) > 0 &&
           !needsApproval() &&
           !missionState.isLaunching;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getCommanderTitle = (rank) => {
    if (rank >= 50) return 'Galactic Emperor';
    if (rank >= 40) return 'Fleet Admiral';
    if (rank >= 30) return 'Space Marshal';
    if (rank >= 20) return 'Star Commander';
    if (rank >= 10) return 'Mission Captain';
    return 'Space Cadet';
  };

  const getDestinationName = (destination) => {
    return SPACE_DESTINATIONS[destination]?.name || 'UNKNOWN';
  };

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  if (!connected) {
    return (
      <div className="galactic-command-center">
        <div className="cosmic-particles">
          {cosmicParticles.map(particle => (
            <div
              key={particle.id}
              className="particle-effect"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                opacity: particle.opacity,
                fontSize: `${particle.size}px`,
                transform: `rotate(${particle.rotation}deg)`,
                animationDelay: `${particle.id * 0.1}s`
              }}
            >
              {particle.type}
            </div>
          ))}
        </div>
        <div className="space-access-denied">
          <div className="access-icon">🛸</div>
          <h3 className="access-title">🚫 ACCESS DENIED</h3>
          <p className="access-desc">Commander credentials required to access Galactic Command Center</p>
        </div>
      </div>
    );
  }

  if (!contracts.spaceMission || !contracts.blbToken) {
    return (
      <div className="galactic-command-center">
        <div className="cosmic-particles">
          {cosmicParticles.map(particle => (
            <div
              key={particle.id}
              className="particle-effect"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                opacity: particle.opacity,
                fontSize: `${particle.size}px`,
                transform: `rotate(${particle.rotation}deg)`,
                animationDelay: `${particle.id * 0.1}s`
              }}
            >
              {particle.type}
            </div>
          ))}
        </div>
        <div className="space-access-denied">
          <div className="access-icon">🛰️</div>
          <h3 className="access-title">🔌 CONNECTING TO MISSION CONTROL</h3>
          <p className="access-desc">Establishing quantum link with space command systems...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="galactic-command-center">
      {/* Cosmic Particle Background */}
      <div className="cosmic-particles">
        {cosmicParticles.map(particle => (
          <div
            key={particle.id}
            className="particle-effect"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              opacity: particle.opacity,
              fontSize: `${particle.size}px`,
              transform: `rotate(${particle.rotation}deg)`,
              animationDelay: `${particle.id * 0.1}s`
            }}
          >
            {particle.type}
          </div>
        ))}
      </div>

      {/* Command Header */}
      <div className="command-header-console">
        <button 
          className="space-exit-btn" 
          onClick={() => {
            playGameSound('controlPanel', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('spaceAmbient', 0.2)}
        >
          ← EXIT SPACE
        </button>
        <div className="command-title-section">
          <h2 className="command-main-title">🚀 GALACTIC COMMAND CENTER</h2>
          <div className="command-subtitle">Deep Space Mission Control</div>
        </div>
        <div className="fuel-reserves-display">
          ⛽ {formatNumber(missionState.balance)} BLB
          <div className="commander-rank-display">
            {getCommanderTitle(missionState.commanderRank)}
          </div>
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('controlPanel', 0.2);
            }}
            className="sound-toggle-btn"
            title={soundEnabled ? 'Mute Sounds' : 'Enable Sounds'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {(isOwner || isAdmin) && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('controlPanel', 0.3);
              }}
              onMouseEnter={() => playGameSound('spaceAmbient', 0.2)}
              className={`mission-master-btn ${showAdminPanel ? 'active' : ''}`}
              title="Mission Control Panel"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="space-message space-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="space-message space-success-msg">
          {successMessage}
        </div>
      )}

      <div className="mission-layout">

        {/* Admin Panel */}
        {showAdminPanel && (isOwner || isAdmin) && (
          <div className="mission-master-panel">
            <div className="master-panel-header">
              <h3>🛰️ MISSION CONTROL CHAMBER</h3>
              <div className="master-role-badge">
                {isOwner ? '👑 FLEET COMMANDER' : '🛰️ MISSION DIRECTOR'}
              </div>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="admin-close-btn"
                style={{
                  background: 'rgba(255, 69, 0, 0.3)',
                  border: '2px solid #FF4500',
                  borderRadius: '8px',
                  color: '#FFB347',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem'
                }}
              >
                ✕
              </button>
            </div>

            <div className="admin-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2rem' }}>
              {/* Mission Control Status */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(65, 105, 225, 0.2) 0%, rgba(0, 206, 209, 0.1) 100%)',
                border: '3px solid rgba(65, 105, 225, 0.5)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#4169E1', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🚀 Mission Control Status
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: 'rgba(65, 105, 225, 0.8)', fontWeight: '600' }}>Status:</span>
                  <span style={{
                    color: adminState.isPaused ? '#FF4500' : '#00CED1',
                    fontWeight: '800',
                    textShadow: '0 0 10px currentColor'
                  }}>
                    {adminState.isPaused ? '⏸️ OFFLINE' : '▶️ OPERATIONAL'}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('controlPanel', 0.3);
                      
                      if (contracts.spaceMission) {
                        const tx = adminState.isPaused ? 
                          await contracts.spaceMission.unpauseGame() : 
                          await contracts.spaceMission.pauseGame();
                        
                        setSuccessMessage(`Mission Control ${adminState.isPaused ? 'activated' : 'deactivated'}...`);
                        await tx.wait();
                        
                        await loadAdminData();
                        setSuccessMessage(`✅ Mission Control ${adminState.isPaused ? 'activated' : 'deactivated'} successfully!`);
                      } else {
                        setSuccessMessage(`✅ Mission Control ${adminState.isPaused ? 'activated' : 'deactivated'}! (Demo)`);
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error toggling mission control:', error);
                      setErrorMessage(`Failed to ${adminState.isPaused ? 'activate' : 'deactivate'} mission control: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: adminState.isPaused ? 
                      'linear-gradient(135deg, #00CED1 0%, #4169E1 100%)' : 
                      'linear-gradient(135deg, #FF4500 0%, #DC143C 100%)',
                    border: `3px solid ${adminState.isPaused ? '#00CED1' : '#FF4500'}`,
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: `0 0 15px rgba(${adminState.isPaused ? '0, 206, 209' : '255, 69, 0'}, 0.4)`
                  }}
                >
                  {adminState.isPaused ? '▶️ ACTIVATE' : '⏸️ DEACTIVATE'}
                </button>
              </div>

              {/* Fuel Treasury */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)',
                border: '3px solid rgba(255, 215, 0, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#FFD700', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  ⛽ Fuel Treasury
                </h4>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ color: 'rgba(255, 215, 0, 0.8)', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Current Reserves:
                  </div>
                  <div style={{ 
                    color: '#FFD700', 
                    fontSize: '1.5rem', 
                    fontWeight: '800',
                    textShadow: '0 0 15px currentColor'
                  }}>
                    🚀 {formatNumber(adminState.houseBalance)} BLB
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('controlPanel', 0.3);
                      
                      if (contracts.spaceMission && contracts.spaceMission.emergencyWithdrawAll) {
                        const tx = await contracts.spaceMission.emergencyWithdrawAll();
                        setSuccessMessage('Extracting all fuel reserves...');
                        await tx.wait();
                        
                        await Promise.all([loadAdminData(), loadBalance()]);
                        setSuccessMessage('✅ Emergency fuel extraction completed!');
                      } else {
                        setSuccessMessage('✅ Emergency fuel extraction completed! (Demo)');
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Emergency withdraw error:', error);
                      setErrorMessage(`Emergency extraction failed: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #FF4500 0%, #DC143C 100%)',
                    border: '3px solid #FF4500',
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 15px rgba(255, 69, 0, 0.4)'
                  }}
                >
                  🚨 EMERGENCY EXTRACTION
                </button>
              </div>

              {/* Fund Treasury */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(0, 206, 209, 0.15) 0%, rgba(65, 105, 225, 0.1) 100%)',
                border: '3px solid rgba(0, 206, 209, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#00CED1', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💳 Fund Treasury
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.fundAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, fundAmount: e.target.value }))}
                    placeholder="Amount to fund fuel treasury"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(0, 206, 209, 0.5)',
                      borderRadius: '10px',
                      color: '#00CED1',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.fundAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid fund amount');
                          return;
                        }
                        
                        playGameSound('controlPanel', 0.3);
                        
                        if (contracts.spaceMission && contracts.blbToken) {
                          const amountWei = ethers.utils.parseEther(amount);
                          
                          const allowance = await contracts.blbToken.allowance(walletData.account, contracts.spaceMission.address);
                          if (allowance.lt(amountWei)) {
                            const approveTx = await contracts.blbToken.approve(contracts.spaceMission.address, ethers.constants.MaxUint256);
                            await approveTx.wait();
                          }
                          
                          const tx = await contracts.spaceMission.fundHouse(amountWei);
                          setSuccessMessage('Funding treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB!`);
                        } else {
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, fundAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error funding treasury:', error);
                        setErrorMessage(`Failed to fund treasury: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.fundAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #00CED1 0%, #4169E1 100%)',
                      border: '3px solid #00CED1',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.fundAmount ? 1 : 0.6,
                      boxShadow: adminState.fundAmount ? '0 0 15px rgba(0, 206, 209, 0.4)' : 'none'
                    }}
                  >
                    💎 FUND TREASURY
                  </button>
                </div>
              </div>

              {/* Withdraw Treasury */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.15) 0%, rgba(255, 69, 0, 0.1) 100%)',
                border: '3px solid rgba(255, 165, 0, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#FFA500', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💸 Withdraw Treasury
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.withdrawAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    placeholder="Amount to withdraw"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(255, 165, 0, 0.5)',
                      borderRadius: '10px',
                      color: '#FFA500',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.withdrawAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid withdraw amount');
                          return;
                        }
                        
                        playGameSound('controlPanel', 0.3);
                        
                        if (contracts.spaceMission) {
                          const amountWei = ethers.utils.parseEther(amount);
                          const tx = await contracts.spaceMission.withdrawHouse(amountWei);
                          setSuccessMessage('Withdrawing from treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury!`);
                        } else {
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, withdrawAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error withdrawing from treasury:', error);
                        setErrorMessage(`Failed to withdraw: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #FFA500 0%, #FF4500 100%)',
                      border: '3px solid #FFA500',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.withdrawAmount ? 1 : 0.6,
                      boxShadow: adminState.withdrawAmount ? '0 0 15px rgba(255, 165, 0, 0.4)' : 'none'
                    }}
                  >
                    💸 WITHDRAW
                  </button>
                </div>
              </div>

              {/* Mission Limits */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.15) 0%, rgba(75, 0, 130, 0.1) 100%)',
                border: '3px solid rgba(138, 43, 226, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#8A2BE2', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🎯 Mission Limits
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(138, 43, 226, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MIN FUEL
                      </label>
                      <input
                        type="number"
                        value={adminState.newMinBet || adminState.minBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMinBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(138, 43, 226, 0.5)',
                          borderRadius: '8px',
                          color: '#8A2BE2',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(138, 43, 226, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MAX FUEL
                      </label>
                      <input
                        type="number"
                        value={adminState.newMaxBet || adminState.maxBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMaxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(138, 43, 226, 0.5)',
                          borderRadius: '8px',
                          color: '#8A2BE2',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const minBet = parseFloat(adminState.newMinBet || adminState.minBet);
                        const maxBet = parseFloat(adminState.newMaxBet || adminState.maxBet);

                        if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
                          setErrorMessage('Invalid fuel limits');
                          return;
                        }

                        playGameSound('controlPanel', 0.3);
                        
                        if (contracts.spaceMission) {
                          const minBetWei = ethers.utils.parseEther(minBet.toString());
                          const maxBetWei = ethers.utils.parseEther(maxBet.toString());
                          
                          const tx = await contracts.spaceMission.setBetLimits(minBetWei, maxBetWei);
                          setSuccessMessage('Updating mission limits...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB`);
                        } else {
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error setting mission limits:', error);
                        setErrorMessage(`Failed to set limits: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #8A2BE2 0%, #4B0082 100%)',
                      border: '3px solid #8A2BE2',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(138, 43, 226, 0.4)'
                    }}
                  >
                    🎯 SET LIMITS
                  </button>
                </div>
              </div>

              {/* Mission Configuration */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(25, 25, 112, 0.2) 0%, rgba(72, 61, 139, 0.1) 100%)',
                border: '3px solid rgba(25, 25, 112, 0.5)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#f6f6ffff', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🌌 Mission Configuration
                </h4>
                <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem', fontStyle: 'italic', textAlign: 'center' }}>
                  Configure success rates and payouts for space destinations
                </div>
                
                {/* Success Rates Configuration */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ color: '#f8f8ffff', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    MISSION SUCCESS RATES (%)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    {SPACE_DESTINATIONS.map((destination, index) => (
                      <div key={destination.id}>
                        <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                          {destination.icon} {destination.name} (%)
                        </label>
                        <input
                          type="number"
                          value={adminState.newMissionRates?.[destination.name.toLowerCase()] || '50.0'}
                          onChange={(e) => setAdminState(prev => ({ 
                            ...prev, 
                            newMissionRates: { 
                              ...prev.newMissionRates, 
                              [destination.name.toLowerCase()]: e.target.value 
                            }
                          }))}
                          min="0"
                          max="95"
                          step="0.1"
                          style={{
                            width: '100%',
                            padding: '0.6rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: '2px solid rgba(25, 25, 112, 0.5)',
                            borderRadius: '8px',
                            color: '#f8f8f8ff',
                            fontSize: '0.9rem',
                            fontWeight: '700',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Update Button */}
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('controlPanel', 0.3);
                      setSuccessMessage('✅ Mission configuration updated successfully! (Demo)');
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error updating mission config:', error);
                      setErrorMessage(`Failed to update config: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #191970 0%, #4169E1 100%)',
                    border: '3px solid #191970',
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 15px rgba(25, 25, 112, 0.4)'
                  }}
                >
                  🌌 UPDATE MISSIONS
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Main Mission Area */}
        <div className="mission-command-center">
          {/* Launch Modal */}
          {missionState.showLaunchModal && (
            <div className="launch-modal-overlay">
              <div className="launch-modal">
                <div className="launch-header">
                  <h2>🚀 MISSION LAUNCH SEQUENCE!</h2>
                  <div className="mission-info">
                    <span>Fuel: {formatNumber(missionState.fuelAmount)} BLB</span>
                    <span>Destination: {missionState.selectedDestination !== null ? SPACE_DESTINATIONS[missionState.selectedDestination].name : 'NONE'}</span>
                    <span>Commander: {getCommanderTitle(missionState.commanderRank)}</span>
                  </div>
                </div>

                <div className="launch-display-area">
                  <div className="space-rocket-display">
                    <div className="mission-rocket">
                      <div className="rocket-body">🚀</div>
                      <div 
                        className="rocket-exhaust" 
                        style={{ 
                          transform: `scaleY(${1 + (missionState.systemReadiness / 100)})`,
                          opacity: missionState.systemReadiness / 100
                        }}
                      ></div>
                      <div className="rocket-flames" 
                           style={{ 
                             opacity: missionState.systemReadiness / 100,
                             transform: `translateY(${missionState.systemReadiness / 5}px)`
                           }}>
                        🔥🔥🔥
                      </div>
                    </div>
                    
                    <div className="system-readiness-meter">
                      <div className="meter-label">SYSTEM READINESS</div>
                      <div className="readiness-bar">
                        <div 
                          className="readiness-progress" 
                          style={{ 
                            width: `${missionState.systemReadiness}%`,
                            background: `linear-gradient(90deg, #FF4500 0%, #FFD700 50%, #00FF00 100%)`
                          }}
                        ></div>
                      </div>
                      <div className="readiness-percentage">{missionState.systemReadiness}%</div>
                    </div>
                  </div>

                  {launchPhase === 'launch' && (
                    <div className="rocket-flight-sequence">
                      <div className="flying-rocket">🚀</div>
                      <div className="rocket-vapor-trail"></div>
                    </div>
                  )}
                </div>

                {/* Result Display in Modal */}
                {launchPhase === 'result' && missionOutcome && (
                  <div className="mission-result-display">
                    <div className={`mission-result-content ${missionOutcome.won ? 'mission-success' : 'mission-failure'}`}>
                      <div className="mission-result-icon">
                        {missionOutcome.destination === 4 && missionOutcome.won ? '🌌' : 
                         missionOutcome.won ? SPACE_DESTINATIONS[missionOutcome.destination]?.icon : '💥'}
                      </div>
                      <h3 className="mission-result-title">
                        {missionOutcome.destination === 4 && missionOutcome.won ? '🌌 BLACK HOLE SUCCESS!' : 
                         missionOutcome.won ? `🚀 ${SPACE_DESTINATIONS[missionOutcome.destination]?.name} REACHED!` : '💥 MISSION FAILED!'}
                      </h3>
                      <div className="mission-result-amount">
                        {missionOutcome.won ? `+${formatNumber(missionOutcome.netPayout)}` : `-${formatNumber(missionOutcome.fuelAmount)}`} BLB
                      </div>
                      <div className="mission-result-desc">
                        Destination: {getDestinationName(missionOutcome.destination)}
                      </div>
                      {missionOutcome.destination === 4 && missionOutcome.won && (
                        <div className="black-hole-celebration">
                          🌌 LEGENDARY SPACE ACHIEVEMENT! UNIVERSE CONQUERED! 🌌
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Launch preparation indicator */}
                {launchPhase === 'preparation' && (
                  <div className="launch-preparation-indicator">
                    <div className="preparation-text">🚀 SYSTEMS INITIALIZATION...</div>
                    <div className="preparation-subtext">Engines charging, navigation locked!</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Space View */}
          <div className="space-exploration-area">
            <div className="space-view-display">
              <div className="view-header">
                <span>DEEP SPACE EXPLORATION ZONE</span>
                <span className="space-status">READY FOR MISSION</span>
              </div>
              
              <div className="destination-selection-area" ref={spaceViewRef}>
                <div className="space-destinations-grid">
                  {SPACE_DESTINATIONS.map((destination) => (
                    <div 
                      key={destination.id}
                      className={`destination-planet ${missionState.selectedDestination === destination.id ? 'selected' : ''}`}
                      style={{ borderColor: destination.color }}
                      onClick={() => {
                        playGameSound('starField', 0.3);
                        setMissionState(prev => ({ 
                          ...prev, 
                          selectedDestination: prev.selectedDestination === destination.id ? null : destination.id 
                        }));
                      }}
                      onMouseEnter={() => playGameSound('spaceAmbient', 0.1)}
                    >
                      <div className="planet-icon" style={{ color: destination.color }}>
                        {destination.icon}
                      </div>
                      <div className="planet-details">
                        <div className="planet-name">{destination.name}</div>
                        <div className="planet-distance">{destination.distance}</div>
                        <div className="planet-risk" style={{ color: destination.color }}>
                          {destination.riskLevel} RISK
                        </div>
                        <div className="planet-multiplier">{destination.baseMultiplier}</div>
                      </div>
                      <div className="planet-atmosphere">
                        <small>{destination.atmosphere}</small>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Mission Statistics */}
                <div className="mission-statistics">
                  <div className="stat-entry">
                    <span className="stat-label">MISSIONS:</span>
                    <span className="stat-value">{missionState.totalMissions}</span>
                  </div>
                  <div className="stat-entry">
                    <span className="stat-label">SUCCESSFUL:</span>
                    <span className="stat-value">{missionState.successfulMissions}</span>
                  </div>
                  <div className="stat-entry">
                    <span className="stat-label">SUCCESS RATE:</span>
                    <span className="stat-value">
                      {missionState.totalMissions > 0 ? Math.round((missionState.successfulMissions / missionState.totalMissions) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission Controls */}
          <div className="mission-controls">
            {/* Fuel Amount */}
            <div className="mission-control-panel">
              <label className="control-panel-label">⛽ FUEL ALLOCATION</label>
              <div>
                <input
                  className="fuel-amount-input"
                  type="number"
                  value={missionState.fuelAmount}
                  onChange={(e) => setMissionState(prev => ({ ...prev, fuelAmount: e.target.value }))}
                  disabled={missionState.isLaunching}
                  min="1"
                  max={missionState.balance}
                  step="1"
                  onFocus={() => playGameSound('controlPanel', 0.1)}
                />
                <div className="fuel-preset-buttons">
                  {['10', '50', '200'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('controlPanel', 0.2);
                        setMissionState(prev => ({ ...prev, fuelAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('spaceAmbient', 0.1)}
                      disabled={missionState.isLaunching}
                      className="fuel-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('controlPanel', 0.2);
                      setMissionState(prev => ({ ...prev, fuelAmount: Math.floor(parseFloat(prev.balance)).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('spaceAmbient', 0.1)}
                    disabled={missionState.isLaunching}
                    className="fuel-preset-btn all-fuel-btn"
                  >
                    MAX FUEL
                  </button>
                </div>
              </div>
            </div>

            {/* Selected Destination Info */}
            {missionState.selectedDestination !== null && (
              <div className="mission-control-panel">
                <label className="control-panel-label">🎯 SELECTED DESTINATION</label>
                <div className="selected-destination-info">
                  <div className="destination-header">
                    <span className="destination-icon" style={{ color: SPACE_DESTINATIONS[missionState.selectedDestination].color }}>
                      {SPACE_DESTINATIONS[missionState.selectedDestination].icon}
                    </span>
                    <div className="destination-name">
                      {SPACE_DESTINATIONS[missionState.selectedDestination].name}
                    </div>
                  </div>
                  <div className="destination-description">
                    {SPACE_DESTINATIONS[missionState.selectedDestination].description}
                  </div>
                  <div className="destination-specs">
                    <div>Distance: {SPACE_DESTINATIONS[missionState.selectedDestination].distance}</div>
                    <div>Risk: {SPACE_DESTINATIONS[missionState.selectedDestination].riskLevel}</div>
                    <div>Reward: {SPACE_DESTINATIONS[missionState.selectedDestination].baseMultiplier}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Button */}
            <div className="mission-control-panel">
              {needsApproval() ? (
                <button
                  className="mission-action-btn mission-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('spaceAmbient', 0.2)}
                  disabled={missionState.isApproving}
                >
                  {missionState.isApproving ? '🛰️ AUTHORIZING...' : '🚀 AUTHORIZE MISSION'}
                </button>
              ) : (
                <button
                  className="mission-action-btn mission-launch-btn"
                  onClick={handleLaunchMission}
                  onMouseEnter={() => playGameSound('spaceAmbient', 0.2)}
                  disabled={!canLaunchMission()}
                >
                  {missionState.isLaunching ? '🚀 LAUNCHING...' : '🚀 LAUNCH MISSION'}
                </button>
              )}
            </div>

            {/* Potential Rewards */}
            <div className="potential-rewards-display">
              🌟 POTENTIAL REWARDS: 2x - 100x your fuel investment
            </div>
          </div>
        </div>

        {/* Mission Leaderboard */}
        <div className="mission-leaderboard">
          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🚀 YOUR PROFILE</h3>
            <div className="leaderboard-entry">
              <span className="entry-label">Rank</span>
              <span className="entry-value">{missionState.commanderRank}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Points</span>
              <span className="entry-value">{formatNumber(missionState.missionPoints)}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Title</span>
              <span className="entry-value">{getCommanderTitle(missionState.commanderRank)}</span>
            </div>
          </div>

          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🌌 SPACE COMMAND</h3>
            <div className="leaderboard-entry">
              <span className="entry-label">Active</span>
              <span className="entry-value">{spaceStats.activeExplorers}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Missions</span>
              <span className="entry-value">{formatNumber(spaceStats.totalMissions)}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Rewards</span>
              <span className="entry-value">{formatNumber(spaceStats.totalRewards)}</span>
            </div>
          </div>

          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">📡 MISSION PROTOCOL</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                'Select destination',
                'Allocate fuel',
                'Launch mission',
                'Reach for the stars!'
              ].map((protocol, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #4169E1, #00CED1)', 
                    color: '#FFF', 
                    width: '2rem', 
                    height: '2rem', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: '800', 
                    fontSize: '0.9rem', 
                    flexShrink: 0,
                    boxShadow: '0 0 8px rgba(65, 105, 225, 0.4)'
                  }}>{index + 1}</div>
                  <div style={{ color: 'rgba(0, 206, 209, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {protocol}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {missionHistory.length > 0 && (
            <div className="leaderboard-panel">
              <h3 className="leaderboard-title">📡 RECENT MISSIONS</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {missionHistory.map((mission, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(65, 105, 225, 0.1)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${mission.won ? '#00CED1' : '#FF4500'}`,
                    border: '1px solid rgba(65, 105, 225, 0.3)'
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {mission.won ? SPACE_DESTINATIONS[mission.destination]?.icon : '💥'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: 'rgba(0, 206, 209, 0.8)', fontSize: '0.8rem', fontWeight: '600' }}>
                          {mission.won ? SPACE_DESTINATIONS[mission.destination]?.name : 'MISSION FAILED'}
                        </div>
                        <div style={{ 
                          color: mission.won ? '#00CED1' : '#FF4500', 
                          fontWeight: '800', 
                          fontSize: '0.9rem'
                        }}>
                          {mission.won ? `+${formatNumber(mission.rewards)}` : 'LOST'}
                        </div>
                      </div>
                      <div style={{ color: 'rgba(65, 105, 225, 0.6)', fontSize: '0.7rem' }}>
                        Fuel: {formatNumber(mission.fuelAmount)} BLB
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpaceMission;